import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { fromJS } from "immutable";
import PropTypes from "prop-types";
import { AddSmartfocusContainer } from "./smartfocus-components";
import Typography from "../../../../common-components/Typography";
import vmTheme from "../../../../constants/theme";
import { font } from "../../../../constants/font";
import { STATIC_PATH } from "../../../../constants/config";
import Action from "../../../../common-components/Action";
import { updateProjectData, updateTimelineTime } from "../../../../redux/actions/timelineUtils";
import { buildLocalSmartfocus, randomString } from "../../../timeline/timeline-helper";
import { checkZoomSpace } from "../../../../helper/smartfocusHelper";
import useMobileDetection from "../../../../helper/hooks/useMobileDetection";
import { showFocusSpaceErrorModal } from "../../../../redux/actions/modalAction";
import { setSelectedItems, setSelectedZoomId } from "../../../../redux/actions/appUtils";

const AddZoomComponent = ({ text, subText }) => {
  const theme = useSelector((state) => state.app.get("theme"));
  const playhead = useSelector((state) => state.app.get("playhead")); // Get the current playhead time
  const isMobile = useMobileDetection();

  const selectedWorkspaceItem = useSelector((state) => {
    return state.app.get("selectedItems");
  });

  const selectedValue = useSelector((state) => state.app.get("selectedSubtitles"));
  const selectedItem = selectedValue.size > 0 ? selectedValue?.get(0)?.split("-")[0] : selectedWorkspaceItem.get(0);
  const item = useSelector((state) =>
    state.projectDetails.getIn(["workspaceItems", selectedItem])
  );

  const project = useSelector((state) => state.projectDetails);

  const dispatch = useDispatch();

  const onSave = async () => {
    if (!item || playhead === undefined || playhead < item.get("enterStart")) return;
    const zoomData = item.get("zoomData")?.toJS() || {};
    const maxDuration = 1; // Maximum allowed duration
    const result = await checkZoomSpace(playhead, zoomData, item, maxDuration);

    if (result.status) {
      const enterStart = item.get("enterStart");
      const videoStart = item.get("videoStart") || 0;
      const mediaRelativePlayhead = playhead - enterStart + videoStart;

      const newZoomId = `${randomString("zoom")}`;
      const duration = result.after
        ? result.after.start - playhead
        : item.get("videoEnd") - (playhead - enterStart);

      const defaultX = project.get("height") > project.get("width") ? 118.63636363636363 : 202.89811643835617;
      const defaultY = project.get("height") > project.get("width") ? 210.52631578947367 : 127.89473684210526;

      const clampedDuration = Math.max(0.5, Math.min(duration, 3));
      const newZoomOutEnd = mediaRelativePlayhead + clampedDuration;
      let disableZoomOut = false;

      // Check if new zoomOut end matches any existing zoomIn start
      Object.entries(zoomData).forEach(([key, group]) => {
        const { zoomIn } = group;
        if (zoomIn && zoomIn.start === newZoomOutEnd) {
          disableZoomOut = true;
          zoomData[key].zoomOut.disableZoomIn = true; // Set disableZoomIn for the zoomOut type
        }
      });

      const newZoomData = {
        zoomIn: {
          start: mediaRelativePlayhead,
          end: mediaRelativePlayhead + 0.5,
          height: project.get("height") * 0.5, // 50% height
          width: project.get("width") * 0.5,  // 50% width
          x: defaultX,
          y: defaultY,
          type: "zoomIn",
        },
        zoomOut: {
          start: (mediaRelativePlayhead + clampedDuration) - 0.5,
          end: newZoomOutEnd,
          height: project.get("height"),
          width: project.get("width"),
          x: 0,
          y: 0,
          type: "zoomOut",
          disableZoomOut, // Set disableZoomOut if needed
        },
      };

      const updatedZoomData = { ...zoomData, [newZoomId]: newZoomData };

      const updatedProject = project.setIn(
        ["workspaceItems", item.get("id"), "zoomData"],
        fromJS(updatedZoomData)
      );

      const toUpdate = [
        {
          id: item.get("id"),
          container: "workspaceItems",
          toUpdate: {
            zoomData: updatedZoomData,
          },
        },
      ];

      if (toUpdate.length) {
        dispatch(updateTimelineTime({ toUpdate }));
        dispatch(setSelectedItems({
          selectedSubtitles: [`${item.get("id")}-${newZoomId}`],
          propWindowType: "SMART_FOCUS"
        }));
        dispatch(setSelectedZoomId(`${item.get("id")}-${newZoomId}`));
        const updatedLocalZoom = buildLocalSmartfocus(updatedProject);
        const updatedProjects = updatedLocalZoom.getIn(["localZoom"]);
        dispatch(
          updateProjectData({
            localZoom: updatedProjects,
          })
        );
      }
    } else {
      dispatch(showFocusSpaceErrorModal({ show: true }));
    }
  };

  return (
    <div>
      <AddSmartfocusContainer>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <img
            src={`${STATIC_PATH}add-smartfocus-icon.svg`}
            alt="Zoom Icon"
            style={{ width: "48px", height: "48px", alignSelf: "center" }}
          />
          <Typography
            content={text}
            margin="0px 0px 0px 17px !important"
            color={vmTheme[theme].panelPrimaryColor}
            font={font.boldBase}
            height="19px"
            align="center"
          />
          <Typography
            content={subText}
            margin="0px 0px 0px 17px !important"
            color={vmTheme[theme].sbSecondaryColor}
            font={font.normalBase_15}
            height="19px"
            align="center"
            enableTrim={false}
          />
        </div>
        <Action
          className={"addzoom-btn"}
          background={vmTheme[theme].polarColor}
          borderRadius="8px"
          font={font.normalBase_medium_16}
          fontColor={vmTheme[theme].tabTextColor}
          width={isMobile ? "100%" : "284px"}
          height="40px"
          margin="16px 0px 8px"
          onClick={onSave}
          text={"Add Zoom"}
          borderWidth={`1px`}
          borderColor={vmTheme[theme].searchBorder}
          customBeforeIcon={`${STATIC_PATH}${vmTheme[theme].icons.smartFocusIcon}`}
          iconLeft={isMobile ? "8.2rem" : "97px"}
        />
      </AddSmartfocusContainer>
    </div>
  );
};


AddZoomComponent.propTypes = {
  text: PropTypes.string,
  subText: PropTypes.string
};

export default AddZoomComponent;
